import type { MotionValue, SpringOptions } from "motion";
import { frame, useSpring, useTransform, type MotionStyle, type TransformInputRange, type TransformOptions } from "motion/react";
import { useEffect, useRef, type RefObject } from "react";

type TransformerArgs<T extends number | string> = {
  key: string;
  inputRange: TransformInputRange;
  outputRange: T[];
  options?: TransformOptions<T>;
};

type AnimatedElementType = {
  ref: React.RefObject<HTMLElement | null>;
  scrollYProgress: MotionValue<number>;
  transformers?: TransformerArgs<number | string>[];
  style?: MotionStyle;
};

class AnimatedElement {
  public readonly ref: AnimatedElementType["ref"];
  private transformers: AnimatedElementType["transformers"]; // Save pre-initialised transformer properties
  private style: AnimatedElementType["style"];
  private scrollYProgress: MotionValue<number>;

  constructor({ ref, scrollYProgress, transformers, style }: AnimatedElementType) {
    this.ref = ref;
    this.scrollYProgress = scrollYProgress;
    this.transformers = transformers;
    this.style = style;
  }

  // Adds a transformer that this element is affected by
  public addTransformer = (transformFunction: TransformerArgs<number | string>) => {
    // Update transformers
    this.transformers = this.transformers ? [...this.transformers, transformFunction] : [transformFunction];
  };

  public getTransformers = () => {
    return this.transformers;
  };

  // Initialises all transformers with input range offsets, appends them into the style object, and returns the result
  public initTransformers = (scrollYProgress: MotionValue<number>, rangeOffset: [number, number]) => {
    // Iterate through transformers
    const result = this.transformers?.map((args) => {
      // Calculate the new input ranges using the offset
      const newInputs = args.inputRange.map((v, i) => v + (rangeOffset?.[i] ?? 0));

      // Initialise a new useTransform using the args
      const output = useTransform(scrollYProgress, newInputs, args.outputRange, args.options);

      // Append to styles
      this.style = { ...this.style, [args.key]: output };

      return { [args.key]: output };
    });

    return result;
  };

  /**
   * Returns MotionValue styles
   * Also initialises all transformers if haven't already
   */
  public getStyle = () => {
    if (this.style == undefined) {
      // Initialise transformers
      this.initTransformers(this.scrollYProgress, [0, 0]);
    }

    if (this.style) {
      return this.style;
    }
  };
}

/**
 * Class for plotting and executing scroll-based animations using framer-motion
 */
export default class AnimationSequence {
  /**
   * (The below mentioned functionality is not yet implemented)
   * Store all the refs to elements involved in this animation sequence
   * Elements grouped together will have the same offset applied to them
   *
   * Example:
   * animatedElement1: inputRange of [0.1, 0.2]
   * animatedElement2: inputRange of [0.1, 0.2]
   * animatedElement3: inputRange of [0.2, 0.3]
   * animatedElement4: inputRange of [0.2, 0.3]
   *
   * [
   *    [
   *        animatedElement1
   *    ],
   *    [
   *        animatedElement2,
   *        animatedElement3
   *    ],
   *    [
   *        animatedElement4
   *    ]
   * ]
   *
   * initialiseTransformers() is called
   *
   * animatedElement1: useTransform() is called with an inputRange of [0.1, 0.2] - (+0)
   * animatedElement2: useTransform() is called with an inputRange of [0.2, 0.3] - (+0.1)
   * animatedElement3: useTransform() is called with an inputRange of [0.3, 0.4] - (+0.1)
   * animatedElement4: useTransform() is called with an inputRange of [0.4, 0.5] - (+0.2)
   */
  private elements: AnimatedElement[][] = [];
  private scrollYProgress: MotionValue<number>;

  constructor(scrollYProgress: MotionValue<number>) {
    this.scrollYProgress = scrollYProgress;
  }

  /**
   * Adds a motion element to the animation sequence
   */
  public add = (ref?: React.RefObject<HTMLElement | null>, transformers?: Parameters<AnimatedElement["addTransformer"]>["0"][]) => {
    if (!ref) {
      ref = useRef(null);
    }

    const animatedElement = new AnimatedElement({
      ref,
      scrollYProgress: this.scrollYProgress,
      transformers,
    });

    this.elements.push([animatedElement]);
    return animatedElement;
  };

  /**
   * Adds multiple transformer functions to an animated element
   */
  public addTransformers = (ref: React.RefObject<HTMLElement>, transformer: Parameters<AnimatedElement["addTransformer"]>["0"][]) => {
    // Find the element to add the transformer to
    const element: AnimatedElement | null = this.get(ref);

    if (!element) {
      throw new Error("Unable to animate element: ", {
        cause: ref,
      });
    }

    // Iterate through each transformer and add it
    transformer.forEach((v) => {
      element.addTransformer(v);
    });
  };

  /**
   * Adds a transformer function to an animated element
   */
  public addTransformer = (ref: React.RefObject<HTMLElement>, transformer: Parameters<AnimatedElement["addTransformer"]>[0]) => {
    this.addTransformers(ref, [transformer]);
  };

  /**
   * Searches through the elements stack for an element
   */
  public get = (target: React.RefObject<HTMLElement>) => {
    // Iterate through parent arrays
    for (const array of this.elements) {
      // Iterate through children
      for (const element of array) {
        // Check if this is the element we want
        if (element.ref === target) {
          // Element found
          return element;
        }
      }
    }

    return null;
  };

  public initTransformers = (scrollYProgress: MotionValue<number>) => {
    // Iterate through all elements
    const result = this.elements.map((arr) => {
      const childArr = arr.map((e) => {
        e.initTransformers(scrollYProgress, [0, 0]);
      });

      return childArr;
    });

    return result;
  };
}

/**
 * Hook that makes an element follow the pointer
 * Taken from https://motion.dev/examples/react-follow-pointer-with-spring?platform=react
 * @param spring Motion spring configuration
 * @param ref Ref to the element
 * @returns MotionValues for x and y positions
 */
export function useFollowPointer(
  spring: SpringOptions,
  ref: RefObject<HTMLDivElement | null>,
  constraints?: { top: number; left: number; right: number; bottom: number },
) {
  const x = useSpring(0, spring);
  const y = useSpring(0, spring);

  useEffect(() => {
    if (!ref.current) return;

    const handlePointerMove = ({ clientX, clientY }: MouseEvent) => {
      const element = ref.current!;
      const rect = element.getBoundingClientRect();

      frame.read(() => {
        // Calculate new position
        let newX = clientX - rect.left - rect.width / 2;
        let newY = clientY - rect.top - rect.height / 2;

        // Check if new position exceeds constraints
        // If exceeds, set to constraint limit
        if (constraints) {
          if (newX < constraints.left) newX = constraints.left;
          if (newX > constraints.right) newX = constraints.right;
          if (newY < constraints.top) newY = constraints.top;
          if (newY > constraints.bottom) newY = constraints.bottom;
        }

        x.set(newX);
        y.set(newY);
      });
    };

    window.addEventListener("pointermove", handlePointerMove);

    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, [constraints]);

  return { x, y };
}
